import { useFormik } from 'formik';
import { useSendAlert } from '../utilities/send-alert';
import * as yup from 'yup';
import { parseGraphqlError } from '../utilities/parse-graphql-error';
import axios from 'axios';
import { useState } from 'react';
import { PointerIcon, SupportIcon } from '../constants/asset-constants';
import { isDisposableEmail } from '../utilities/is-disposable-email';

export const ContactForm = ({ serviceTitle, isHomeForm = false }) => {
  const [submitLoading, setSubmitLoading] = useState(false);
  const [formLoadedAt] = useState(Date.now());
  const sendAlert = useSendAlert();
  const SLACK_URL = process.env.REACT_APP_SLACK_API;

  const {
    values,
    errors,
    touched,
    handleChange,
    handleSubmit,
    handleBlur,
    resetForm
  } = useFormik({
    initialValues: {
      phoneNumber: '',
      firstName: '',
      question: '',
      email: '',
      company_url: ''
    },
    validationSchema: yup.object().shape({
      phoneNumber: yup.number().required('Please enter a phone number'),
      firstName: yup
        .string()
        .min(2, 'Name must be at least 2 characters')
        .max(50, 'Name must be at most 50 characters')
        .matches(
          /^[a-zA-ZÀ-ÖØ-öø-ÿ]+([ '-][a-zA-ZÀ-ÖØ-öø-ÿ]+)*$/,
          'Please enter a valid name'
        )
        .matches(/[aeiouyAEIOUY]/, 'Please enter a valid name')
        .test(
          'no-consonant-clusters',
          'Please enter a valid name',
          value =>
            !value ||
            !/[bcdfghjklmnpqrstvwxzBCDFGHJKLMNPQRSTVWXZ]{4,}/.test(value)
        )
        .test(
          'no-consecutive-repeats',
          'Please enter a valid name',
          value => !value || !/(.)\1\1/.test(value)
        )
        .test(
          'no-q-consonant',
          'Please enter a valid name',
          value =>
            !value ||
            !/[qQ][bcdfghjklmnpqrstvwxzBCDFGHJKLMNPQRSTVWXZ]/.test(value)
        )
        .required('Please enter a name'),
      question: yup
        .string()
        .test(
          'real-question',
          'Please enter a valid question or message',
          value => !value || !(value.length > 15 && !/\s/.test(value.trim()))
        )
        .required('Please enter your question'),
      email: yup
        .string()
        .email('Please enter a valid email')
        .test('no-dot-spam', 'Please enter a valid email', value => {
          if (!value) return true;
          const username = value.split('@')[0];
          const dotCount = (username.match(/\./g) || []).length;
          return dotCount <= 2;
        })
        .test(
          'no-disposable-email',
          'Please enter a valid permanent email',
          value => !value || !isDisposableEmail(value)
        )
        .required('Please enter your email')
    }),
    onSubmit: async (values, { resetForm: formReset }) => {
      // 1. Honeypot check: to ensure the form is filled by a human n not a bot
      if (values.company_url) {
        sendAlert(
          'Question submitted successfully. We would contact you shortly!',
          'success'
        );
        if (formReset) {
          formReset();
        } else {
          resetForm();
        }
        return;
      }

      // 2. Timing check: bots submit almost instantaneously (< 2s)
      const secondsSinceLoad = (Date.now() - formLoadedAt) / 1000;
      if (secondsSinceLoad < 2) {
        sendAlert(
          'Question submitted successfully. We would contact you shortly!',
          'success'
        );
        if (formReset) {
          formReset();
        } else {
          resetForm();
        }
        return;
      }

      setSubmitLoading(true);

      try {
        const response = await axios.post(SLACK_URL, {
          text: `First Name: ${values.firstName}\nPhone Number: ${values.phoneNumber}\nEmail Address: ${values.email}\nQuestion: ${values.question}\nService: ${serviceTitle}`,
          channel: 'merchant-acquisition'
        });

        if (response.data) {
          setSubmitLoading(false);
          sendAlert(
            'Question submitted successfully. We would contact you shortly!',
            'success'
          );
          resetForm();
        }
      } catch (error) {
        console.error('[Contact Form Submit] Error posting to Slack:', error);
        setSubmitLoading(false);
        sendAlert(parseGraphqlError(error), 'error');

        throw error;
      }
    }
  });
  return (
    <div id='contact-form' className='get-help-form'>
      <div
        className={`get-help-form__contact-form
      ${isHomeForm ? 'blue-bg' : ''}
      `}
      >
        {isHomeForm ? (
          <div className='pointer-div'>
            <img src={PointerIcon} alt='' />
          </div>
        ) : null}

        <div className='lets-chat'>
          <h2 className='heading mx-0 mb-2'>Contact Sales</h2>
          <p className='paragraph'>
            Need answers or assistance, we're here to help.
          </p>
          {isHomeForm ? null : (
            <div className='mt-4'>
              <a
                style={{
                  textDecoration: 'none'
                }}
                href='mailto: hello@topship.africa'
              >
                <div className='new-home-page__link-button bttn-sm bttn-blue contact-support'>
                  <img
                    style={{
                      paddingRight: '4px'
                    }}
                    src={SupportIcon}
                    alt=''
                  />
                  Contact Support
                </div>
              </a>
            </div>
          )}
        </div>
        <div className='form-container'>
          <form onSubmit={handleSubmit}>
            <div
              style={{
                opacity: 0,
                position: 'absolute',
                top: 0,
                left: 0,
                height: 0,
                width: 0,
                zIndex: -1,
                overflow: 'hidden',
                pointerEvents: 'none'
              }}
              aria-hidden='true'
            >
              <input
                type='text'
                name='company_url'
                tabIndex={-1}
                autoComplete='off'
                value={values.company_url}
                onChange={handleChange}
              />
            </div>
            <div className='form-row'>
              <div className='form-group'>
                <label htmlFor='firstName'>
                  Name <span className='required'>*</span>
                </label>
                <input
                  type='text'
                  id='firstName'
                  name='firstName'
                  value={values.firstName}
                  placeholder='Enter name'
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
                {(touched.firstName || values.firstName) &&
                  errors.firstName && (
                    <span className='validation-error text-left footer-error-message'>
                      {errors.firstName}
                    </span>
                  )}
              </div>
              <div className='form-group'>
                <label htmlFor='phoneNumber'>
                  Phone Number <span className='required'>*</span>
                </label>
                <input
                  type='number'
                  id='phoneNumber'
                  name='phoneNumber'
                  value={values.phoneNumber}
                  placeholder='Enter phone number'
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
                {(touched.phoneNumber || values.phoneNumber) &&
                  errors.phoneNumber && (
                    <span className='validation-error text-left footer-error-message'>
                      {errors.phoneNumber}
                    </span>
                  )}
              </div>
            </div>
            <div className='form-group'>
              <label htmlFor='email'>
                Email address <span className='required'>*</span>
              </label>
              <input
                type='email'
                id='email'
                name='email'
                value={values.email}
                placeholder='Enter email address'
                onChange={handleChange}
                onBlur={handleBlur}
              />
              {(touched.email || values.email) && errors.email && (
                <span className='validation-error text-left footer-error-message'>
                  {errors.email}
                </span>
              )}
            </div>
            <div className='form-group mt-1'>
              <label htmlFor='question'>
                How can we help you? <span className='required'>*</span>
              </label>
              <textarea
                id='question'
                name='question'
                rows='4'
                placeholder='Enter question'
                value={values.question}
                onChange={handleChange}
                onBlur={handleBlur}
              ></textarea>
              {(touched.question || values.question) && errors.question && (
                <span className='validation-error text-center footer-error-message'>
                  {errors.question}
                </span>
              )}
            </div>
            <button
              style={{
                width: '100%'
              }}
              disabled={submitLoading}
              className='mt-2 new-home-page__link-button bttn-sm bttn-blue'
              type='submit'
            >
              {submitLoading ? 'Submitting...' : 'Submit'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};
